import { useEffect, useState } from 'react'
import { DrawerHeader, Icon, SectionLabel, SideDrawer, Spacer } from '@/components/ui'
import { EnclosureList } from '@/components/Inbox/EnclosureList'
import { documentUrl, fetchInvoiceEnclosures } from '@/hooks/useInbox'
import type { InboxItemRef } from '@/types/inbox'
import { useT } from '@/i18n/useT'
import { formatDate } from '@/utils/inboxFormat'
import type { DatabaseInvoice } from '@/types/database'

/**
 * The original invoice of a Database row, opened from the eye icon in the
 * row's actions — same panel language as the inbox detail drawer.
 */
export function DocumentPreviewDrawer({
  invoice,
  onClose,
}: {
  invoice: DatabaseInvoice | null
  onClose: () => void
}) {
  const { t } = useT()
  const documentId = invoice?.sourceDocumentId
  const invoiceId = invoice?.id
  const [enclosures, setEnclosures] = useState<InboxItemRef[]>([])

  // Best effort: a failing lookup just means no list, never a broken preview
  useEffect(() => {
    setEnclosures([])
    if (!invoiceId || !documentId) return
    let cancelled = false
    fetchInvoiceEnclosures(invoiceId)
      .then((list) => {
        if (!cancelled) setEnclosures(list)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [invoiceId, documentId])

  return (
    <SideDrawer
      open={!!invoice && !!documentId}
      onClose={onClose}
      width={760}
      header={
        <DrawerHeader
          title={invoice?.supplierName || t('Document')}
          subtitle={[invoice?.rechnungsnummer, invoice?.dateInvoice && formatDate(invoice.dateInvoice)]
            .filter(Boolean)
            .join(' · ')}
          onClose={onClose}
        />
      }
    >
      {documentId && (
        <div
          style={{
            border: '1px solid var(--line)',
            borderRadius: 10,
            background: 'var(--field)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 12px',
              borderBottom: '1px solid var(--line2)',
            }}
          >
            <Spacer />
            <a
              className="fx-btn fx-btn--outline fx-btn--sm"
              href={documentUrl(documentId)}
              target="_blank"
              rel="noopener"
            >
              <Icon name="download" size={14} />
              {t('Download')}
            </a>
            <a
              className="fx-btn fx-btn--ghost fx-btn--sm"
              href={documentUrl(documentId, true)}
              target="_blank"
              rel="noopener"
            >
              <Icon name="external" size={14} />
              {t('In a new tab')}
            </a>
          </div>
          <iframe
            src={documentUrl(documentId, true)}
            title={invoice?.supplierName || t('Document')}
            style={{ width: '100%', flex: 1, minHeight: 600, border: 'none', display: 'block' }}
          />
        </div>
      )}
      {enclosures.length > 0 && (
        <div>
          <SectionLabel>{t('Enclosures (expenses)')}</SectionLabel>
          <div style={{ marginTop: 8 }}>
            <EnclosureList items={enclosures} showState />
          </div>
        </div>
      )}
    </SideDrawer>
  )
}
